import React, { useEffect, useState } from 'react'

import update from 'react-addons-update'
import { useToasts } from 'react-toast-notifications'
import styled from 'styled-components'

import { Button } from '@client/components/atoms'
import { getPlatform, isApp } from '@shared/platform'

const Container = styled.div<{ $disabled: boolean }>`
  display: flex;
  flex: 1;

  .apple-button {
    background-color: ${(props): string => props.$disabled ? props.theme.colors.whites.desertStorm : props.theme.colors.greys.darkCodGrey};
    border-color: ${(props): string => props.$disabled ? props.theme.colors.whites.desertStorm : props.theme.colors.greys.darkCodGrey};
    color: ${(props): string => props.$disabled ? props.theme.colors.whites.desertStorm : props.theme.colors.whites.pureWhite};
  }

  .apple-button:hover {
    opacity: 0.85;
  }
`

export interface AppleProfile {
  name?: string
  givenName?: string
  familyName?: string
}

export interface AppleSocialButtonProps {
  disabled: boolean
  action?: 'SIGN UP' | 'LOGIN'
  className?: string
  didAuthenticateWithApple: (token: string, profile?: AppleProfile) => void
}

interface AppleSocialButtonState {
  disabled: boolean
  loading: boolean
}

const DEFAULT_STATE: AppleSocialButtonState = {
  disabled: false,
  loading: false,
}

export function AppleSocialButton({ disabled, action = 'SIGN UP', className, didAuthenticateWithApple }: AppleSocialButtonProps): JSX.Element {
  const { addToast } = useToasts()
  const [state, setState] = useState<AppleSocialButtonState>({ ...DEFAULT_STATE })

  const buttonText = state.disabled ? `APPLE ${action} DISABLED` : `${action}  WITH APPLE`

  useEffect(() => {
    let mounted = true

    if (mounted) {
      const isNativeIOS = isApp() && getPlatform() === 'ios'
      setState((prevState) => update(prevState, {
        disabled: { $set: !isNativeIOS },
      }))
    }

    return function cleanup() {
      mounted = false
    }
  }, [])

  const signInWithCapacitorPlugin = async () => {
    try {
      const { SocialLogin } = await import('@capgo/capacitor-social-login')

      const res = await SocialLogin.login({
        provider: 'apple',
        options: {},
      })

      const token = (res as any)?.result?.idToken || (res as any)?.result?.identityToken
      const profile = (res as any)?.result?.profile

      if (token) {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.debug('Apple login result profile:', profile)
        }
        didAuthenticateWithApple(token, profile)
      } else {
        addToast('Unable to conduct Apple sign-in.', {
          appearance: 'error',
          autoDismiss: true,
        })
      }
    } catch (error) {
      addToast('Unable to conduct Apple sign-in.', {
        appearance: 'error',
        autoDismiss: true,
      })

      console.error('Error during Capacitor Apple login:', JSON.stringify(error))
    } finally {
      _setLoading(false)
    }
  }

  const _handleClick = async (): Promise<void> => {
    const isNativeIOS = isApp() && getPlatform() === 'ios'
    if (isNativeIOS) {
      _setLoading(true)
      signInWithCapacitorPlugin()
    }
  }

  const _setLoading = (loading: boolean): void => {
    setState((prevState) => update(prevState, {
      loading: { $set: loading },
    }))
  }

  const actuallyDisabled = state.disabled || disabled

  return (
    <Container className={className} $disabled={actuallyDisabled}>
      <Button
        title={buttonText}
        disabled={actuallyDisabled || state.loading}
        loading={state.loading}
        onClick={_handleClick}
        className='apple-button'
        fullWidth />
    </Container>
  )
}

